import { useEffect, useState } from "react";

/**
 * Is label scanning worth offering on this device?
 *
 * Two conditions, and both matter:
 *
 *   A CAMERA EXISTS. `enumerateDevices` reports a `videoinput` before any
 *   permission is granted — the labels are blank until then, but the KIND is
 *   not, which is exactly and only what this needs.
 *
 *   THE DEVICE IS HELD, not sat in front of. A laptop webcam is a camera by
 *   this test and a bad one for this job: it is fixed, wide, and low
 *   resolution, and you cannot bring it up to a packet. ADR 0004 measured what
 *   happens when the model gets a soft label image — it does not fail, it
 *   returns the MANUFACTURE date as an expiry. A wrong date is worse than no
 *   date, so a camera that reliably produces one is worse than no camera.
 *
 * Desktop therefore types and talks. Both work; neither is a fallback.
 */
export function useHasCamera(): boolean {
  const [has, setHas] = useState(false);

  useEffect(() => {
    let live = true;

    // A mouse-driven pointer means a machine you sit at. Touchscreen laptops
    // report `coarse` too, but they are also the case where holding the thing
    // up to a packet is at least possible.
    const handheld =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches;

    if (!handheld || !navigator.mediaDevices?.enumerateDevices) {
      return;
    }

    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        if (live) setHas(devices.some((d) => d.kind === "videoinput"));
      })
      // Firefox rejects this in some privacy modes. No camera offered is the
      // right answer to "we cannot tell".
      .catch(() => {});

    return () => {
      live = false;
    };
  }, []);

  return has;
}
